import * as React from 'react';
import styled from 'styled-components';
import { deprecated } from '@ally/metronome-icons';
import { Box, Button, FormText, Icon, Space, TextBody, upToLg, upToMd, } from '@ally/metronome-ui';
import { AriaLive } from '@ally/use-aria-live';
import { useForm } from '@ally/use-form';
import { MaxOTPCodeAttemptsModal, } from './MaxOTPCodeAttemptsModal';
import { otpRules as getOtpRules, maskBy } from '../../../../../Utils';
import { InputWrapper } from '../../../../Shared';
import { LoginHeading } from '../../../Layout/Layout';
import { TrackingEvent } from '../../../../../constants';
const StyledLink = styled(Button) `
  ${({ theme: { media } }) => `
    ${media.down('md')} {
      width: 100%;
      justify-content: center;
    }
  `}
`;
const ErrorTextBody = styled(TextBody) `
  color: #d53540;
`;
const statusMessageRef = React.createRef();
/**
 * Display nothing if Loading
 *
 * Display error if Error && code has _not_ been resent
 *
 * Display re-sent if no status and code resend request was made
 */
export const StatusMessaging = React.forwardRef(({ status, isCodeResent }, ref) => {
    if (status === 'Error' && !isCodeResent) {
        return (React.createElement(AriaLive, null,
            React.createElement(ErrorTextBody, { size: "sm", tag: "span", weight: "bold", ref: ref, tabIndex: -1 }, "The security code you entered does not match the one we sent. If you requested a code more than once, make sure you're entering the latest code. Please try again or call us 24/7 at 1-877-247-2559, and we'll help you.")));
    }
    if (status === 'None' && isCodeResent) {
        return (React.createElement(Box, { display: "flex", alignItems: "flex-start", mb: "sm" },
            React.createElement(Icon, { icon: deprecated.muiSuccessFill, fill: "success", size: "lg" }),
            React.createElement(Space, { ml: "xs" },
                React.createElement(TextBody, { size: "sm", tag: "span", weight: "bold", ref: ref, tabIndex: -1 }, "We re-sent your security code."))));
    }
    return null;
});
export const VerifyCodeErrorBanner = ({ navigateToLogin, otpInputCount }) => {
    if (otpInputCount >= 5) {
        return React.createElement(MaxOTPCodeAttemptsModal, { navigateToLogin: navigateToLogin });
    }
    return null;
};
const FORM_ID = 'ally-login-verify-code-form';
export const VerifyCodeContent = ({ handleResend, hostServices, isInPortal = false, logger, maskedDeliveryOption: { type, value }, navigateToLogin, navigateToSendCode, onComplete, otpInputCount = 0, status, }) => {
    React.useEffect(() => {
        logger === null || logger === void 0 ? void 0 : logger.track(TrackingEvent.TransmitVerifyCodeLoad);
        hostServices === null || hostServices === void 0 ? void 0 : hostServices.analytics.trackPageView({
            eventType: 'pageview',
            pageName: 'AOS:Login:Security:Enter Security Code',
        });
    }, []);
    const shortCode = '25595';
    const [isCodeResent, setIsCodeResent] = React.useState(false);
    const handleFormSubmit = ({ otpCode }) => {
        logger === null || logger === void 0 ? void 0 : logger.track(TrackingEvent.TransmitVerifyCodeSubmit);
        if (otpCode) {
            setIsCodeResent(false);
            onComplete(otpCode);
        }
    };
    const { handleChange, handleSubmit, values, errors, resetFields, resetErrors, } = useForm({
        submit: handleFormSubmit,
        validate: getOtpRules(shortCode),
        initialState: { otpCode: '' },
        formId: FORM_ID,
    });
    const handleResendCode = () => {
        setIsCodeResent(true);
        resetFields('otpCode');
        resetErrors();
        handleResend();
    };
    React.useEffect(() => {
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = 'Leave Site? Changes you made may not be saved.';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);
    React.useEffect(() => {
        if (isCodeResent && statusMessageRef.current) {
            statusMessageRef.current.focus();
        }
    }, [isCodeResent]);
    return (React.createElement(React.Fragment, null,
        React.createElement(LoginHeading, { isInPortal: isInPortal, title: "We need to make sure it's you" }),
        React.createElement(Box, { display: "flex", width: isInPortal ? 12 : upToMd(12, 6), marginBottom: "24px" },
            React.createElement(TextBody, { tag: "p", size: "sm" },
                "We sent a security code to:",
                React.createElement(TextBody, { tag: "p", size: "sm" }, maskBy[type](value)),
                React.createElement(TextBody, { tag: "p", size: "sm" }, "When you get it, enter it below."),
                "The code will expire 20 minutes after you receive it.")),
        React.createElement(Box, { display: "flex", flexDirection: "row", alignItems: "center", width: isInPortal ? 12 : upToLg('sm', 'xs'), mb: "sm" },
            React.createElement(StatusMessaging, { ref: statusMessageRef, status: status, isCodeResent: isCodeResent })),
        React.createElement("form", { method: "post", onSubmit: handleSubmit, autoComplete: "off", id: FORM_ID, noValidate: true },
            React.createElement(Box, { display: "flex", flexWrap: "wrap" },
                React.createElement(InputWrapper, { mb: "md", width: "235px" },
                    React.createElement(Box, null,
                        React.createElement(FormText, { variant: "input", labelContent: "Enter security code", isPrivate: true, id: "otpCode", inputType: "number", pattern: "[0-9]{6}", onChange: handleChange, value: values.otpCode, "data-cy": "otpCode", errorNotification: { message: errors.otpCode } }))),
                React.createElement(Box, { mt: isInPortal ? 'none' : upToMd('none', 'md'), ml: isInPortal ? 'none' : upToMd('none', 'md'), mb: isInPortal ? 'md' : upToMd('md', 'none'), display: "flex", flexDirection: "column", justifyContent: "center" },
                    React.createElement(Button, { allytmln: "confirmItsYou-resendCode", text: "Resend Security Code", variant: "link", mr: upToMd('none', 'md'), onClick: handleResendCode }))),
            React.createElement(Box, { display: "flex", width: upToMd(12, 6), marginBottom: "24px" },
                React.createElement(TextBody, { tag: "p", size: "md" },
                    "If you chose text and don't get your security code within a minute or so, text \"",
                    React.createElement("b", null, "Optin"),
                    "\" to ",
                    shortCode,
                    ". Then, choose",
                    ' ',
                    React.createElement(TextBody, { tag: "span", size: "md", weight: "bold" }, "Resend Security Code."))),
            React.createElement(Button, { allytmln: "confirmItsYou-continue", text: "Continue", type: "submit", variant: "primary", loading: status === 'Loading', fullWidthMd: true, mr: upToMd('none', 'md') }),
            React.createElement(StyledLink, { allytmln: "confirmItsYou-back", mt: upToMd('md', 'none'), variant: "link", text: "Back", onClick: navigateToSendCode })),
        React.createElement(VerifyCodeErrorBanner, { navigateToLogin: navigateToLogin, otpInputCount: otpInputCount })));
};
//# sourceMappingURL=VerifyCode.js.map